import type { AnamnesisTechnicalEventName, AnamnesisTelemetryPayload } from '@/features/encounters/anamnesis/anamnesisTechnicalTelemetry'
import { getAnamnesisTelemetryEnvironment, sanitizeAnamnesisTelemetryPayload } from '@/features/encounters/anamnesis/anamnesisTechnicalTelemetry'

import { ensureConsultationRootTrace, endConsultationRootTrace } from './consultation.trace'
import { isBrowserLangfuseEnabled, buildBrowserLangfuseBaseMetadata } from './langfuse.client'
import { TECHNICAL_EVENT_TO_PIPELINE_SPAN } from './pipeline.spans'
import { getConsultationRootTrace } from './consultation.trace'
import {
  recordDiarizationProcessing,
  recordSpeakerIdentification,
  recordSttProcessing,
  recordStreamingInterruption,
} from './streaming-trace-manager'
import { sanitizeLangfuseMetadata } from './telemetry.sanitizer'
import {
  bindAnamnesisSentryConsultation,
  mirrorAnamnesisTechnicalEventToSentry,
  unbindAnamnesisSentryConsultation,
} from '@/shared/monitoring/anamnesis-sentry'
import { runTelemetrySafely } from './telemetry.helpers'

let activeConsultationId: string | null = null
let activeDoctorId: string | null = null

export const bindAnamnesisLangfuseConsultation = (options: {
  consultationId: string
  doctorId?: string | null
  language?: string
  offlineMode?: boolean
  fallbackMode?: string | null
}): void => {
  activeConsultationId = options.consultationId
  activeDoctorId = options.doctorId ?? null
  const env = getAnamnesisTelemetryEnvironment()
  ensureConsultationRootTrace({
    consultation_id: options.consultationId,
    doctor_id: options.doctorId,
    browser: env.browser,
    device: env.device,
    language: options.language ?? 'pt-BR',
    offline_mode: options.offlineMode,
    fallback_mode: options.fallbackMode ?? null,
  })
  bindAnamnesisSentryConsultation({
    consultationId: options.consultationId,
    doctorId: options.doctorId,
    language: options.language,
    offlineMode: options.offlineMode,
    fallbackMode: options.fallbackMode,
  })
}

export const unbindAnamnesisLangfuseConsultation = (consultationId?: string): void => {
  const id = consultationId ?? activeConsultationId ?? undefined
  if (id) {
    endConsultationRootTrace(id)
    unbindAnamnesisSentryConsultation(id)
  }
  activeConsultationId = null
  activeDoctorId = null
}

/** Langfuse + Sentry — ponto único após breadcrumb em `trackAnamnesisTechnicalEvent`. */
export const mirrorAnamnesisPipelineEvent = (
  name: AnamnesisTechnicalEventName,
  payload: AnamnesisTelemetryPayload = {},
): void => {
  mirrorAnamnesisTechnicalEventToLangfuse(name, payload)
  mirrorAnamnesisTechnicalEventToSentry(name, payload)
}

export const mirrorAnamnesisTechnicalEventToLangfuse = (
  name: AnamnesisTechnicalEventName,
  payload: AnamnesisTelemetryPayload = {},
): void => {
  runTelemetrySafely(() => {
    const consultationId = activeConsultationId
    if (!consultationId || !isBrowserLangfuseEnabled()) return

    const sanitized = sanitizeAnamnesisTelemetryPayload({
      ...getAnamnesisTelemetryEnvironment(),
      ...payload,
    })
    const chunkId = typeof sanitized.chunkId === 'string' ? sanitized.chunkId : undefined

    if (name === 'streaming_restart' || name === 'streaming_max_session') {
      recordStreamingInterruption({
        consultationId,
        reason: name,
        provider: typeof sanitized.provider === 'string' ? sanitized.provider : undefined,
      })
    }

    if (name === 'external_whisper_upstream' && chunkId) {
      recordSttProcessing({
        consultationId,
        chunkId,
        latencyMs: typeof sanitized.latencyMs === 'number' ? sanitized.latencyMs : undefined,
        provider: typeof sanitized.provider === 'string' ? sanitized.provider : 'external-whisper',
        status: typeof sanitized.status === 'string' ? sanitized.status : undefined,
        upstreamOutcome:
          typeof sanitized.upstreamOutcome === 'string' ? sanitized.upstreamOutcome : undefined,
      })
    }

    if (name === 'diarization_failed' && chunkId) {
      recordDiarizationProcessing({
        consultationId,
        chunkId,
        diarizationBackendKind:
          typeof sanitized.diarizationBackendKind === 'string'
            ? sanitized.diarizationBackendKind
            : undefined,
        failed: true,
        reason: typeof sanitized.reason === 'string' ? sanitized.reason : undefined,
      })
    }

    if (
      (name === 'speaker_classified' ||
        name === 'speaker_low_confidence' ||
        name === 'speaker_verification_failed') &&
      chunkId
    ) {
      recordSpeakerIdentification({
        consultationId,
        chunkId,
        speakerKind: typeof sanitized.speakerKind === 'string' ? sanitized.speakerKind : undefined,
        confidence:
          typeof sanitized.diarizationConfidence === 'number'
            ? sanitized.diarizationConfidence
            : undefined,
        lowConfidence: name === 'speaker_low_confidence',
        verificationFailed: name === 'speaker_verification_failed',
      })
    }

    const spanName = TECHNICAL_EVENT_TO_PIPELINE_SPAN[name]
    const root = getConsultationRootTrace(consultationId)
    if (!spanName || !root) return

    const span = root.span({
      name: spanName,
      metadata: sanitizeLangfuseMetadata(
        buildBrowserLangfuseBaseMetadata({
          consultation_id: consultationId,
          event: name,
          ...(chunkId ? { chunk_id: chunkId } : {}),
          ...(activeDoctorId ? { doctor_id: activeDoctorId } : {}),
          ...Object.fromEntries(
            Object.entries(sanitized).map(([key, value]) => [key, value ?? null]),
          ),
        }),
      ),
    })
    const isFailure = name.endsWith('_failed') || name.includes('error') || name.includes('denied')
    span.end({
      ...(isFailure ? { level: 'WARNING' as const } : {}),
    })
  })
}
